/**
 * Lane FX-PIXEL P1 (docs/research/C0_VFX_PIXEL.md, option C; the user liked sample B, 2026-10-11): the shared rules
 * that make skill effects iso pixel art instead of soft "3D" light.
 *
 * - **Stepped clock** (`fxStep`, `FX_FPS` 12; particles `PART_FPS` 15): effects are drawn on held frames, not
 *   recomputed every display frame.
 * - **The pixel pen** (`PixelPen`): a drawing context handed to the effects in place of the world picture's. Every
 *   shape (rects, paths, strokes) is rasterised on the scene grid (G1: one art px = `res` canvas px) into a cell
 *   buffer, its colour snapped to the FX ramps (`FX_RAMPS`, the master palette's families), its alpha turned into a
 *   dither level (solid, 50 % checker, 25 %, gone) instead of a blend, gradients into banded dithered tones. On a flush
 *   (before any picture is drawn, and at the end of the pass) the cells go out as hard runs, with a 1-px dark outline
 *   (the cell's ramp's darkest tone) round every solid shape. Pictures (`drawImage`) are snapped to the grid and
 *   faded by a cached dithered copy, never by alpha.
 *
 * Nothing here runs per art made; the pen's cost is the cells the effects cover (rows of spans), measured in the
 * lane. Off (the `fxPixel` setting): the effects draw straight on the world picture as before.
 */
import { canvasRevision } from '../pixel';
import { oklab } from '../truepx/palette';

// ── Switch ──

let enabled = false;
/** Turns the pixel rules on or off (the `fxPixel` setting, fx/fxPixel.setting.ts). */
export function setFxPixel(on: boolean): void {
  enabled = on;
}
export const fxPixelOn = (): boolean => enabled;
/** A forced choice for review captures (dev: `__fxPixel(true | false | null)`), over the setting. */
let forced: boolean | null = null;
/** Whether the rules are wanted this frame: the forced choice, else the setting. */
export const fxPixelWanted = (setting: boolean | undefined): boolean => forced ?? setting === true;
/** Cells and pictures the pens drew since the counters were last read (dev: `__fxPixelStats()`). */
const STATS = { cells: 0, images: 0 };
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  const w = window as unknown as { __fxPixel: (v: boolean | null) => void; __fxPixelStats: () => { cells: number; images: number } };
  w.__fxPixel = (v) => (forced = v);
  w.__fxPixelStats = () => {
    const r = { ...STATS };
    STATS.cells = STATS.images = 0;
    return r;
  };
}

// ── 1. Stepped clock ──

/** Effect frames per second: a drawing is held for 5 display frames at 60 Hz. */
export const FX_FPS = 12;
/** Particle steps per second (fast sparks a little finer). */
export const PART_FPS = 15;
/** An effect's age held to its frame (off: unchanged). */
export const fxStep = (t: number): number => (enabled && t > 0 ? Math.floor(t * FX_FPS) / FX_FPS : t);

// ── 2. FX ramps on the master palette (dark → light; index 0 is the outline) ──

export const FX_RAMPS: Readonly<Record<string, readonly string[]>> = {
  ink: ['#0e0a10', '#1b1315', '#2a2026', '#3b3038', '#4e4250', '#6a5e6c'],
  maroon: ['#2a0a14', '#461420', '#671f2c', '#87232a', '#c4272d', '#e55546', '#f48a6c', '#fbc4a8'],
  fire: ['#2e0e0c', '#70261a', '#a8401e', '#d8682a', '#f09a48', '#f8c878', '#fdecc0'],
  white: ['#3a2c36', '#5e4c5a', '#84707c', '#ab979e', '#ceb8b8', '#e0cbc9', '#eddfda', '#f9ebe7', '#fdf6ec'],
  brown: ['#1b1315', '#2c1c1e', '#3b2522', '#573b34', '#7c5f57', '#a08474', '#c8ae98'],
  gold: ['#2e1e0e', '#6a4622', '#9a6732', '#c08c48', '#e4b575', '#f6dca0', '#fff1cc'],
  trim: ['#3a2812', '#6e4e1e', '#9a7430', '#c5993c', '#d6a640', '#f0d27a', '#fbeaa8'],
  ochre: ['#2e240a', '#7a6020', '#a88a2c', '#d0b440', '#ecd870', '#fff2b0'],
  jade: ['#0f2a2a', '#1a4644', '#2a6660', '#42897c', '#6cae98', '#a4d4bc', '#dcf2e2'],
  leaf: ['#0e2410', '#1f4636', '#2f6048', '#4f8d64', '#86bf7a', '#cfe8a0'],
  violet: ['#1e1430', '#34234e', '#503a70', '#725894', '#9a80b4', '#c8b0d6', '#efe2f4'],
  steel: ['#1e2430', '#343e50', '#4e5c70', '#708094', '#9aa8b8', '#c8d2dc', '#e2e8ea'],
  azure: ['#121f3c', '#1d3f6e', '#2f6aa0', '#4f9cc8', '#8ccbe6', '#cdeaf4', '#e2f2f0'],
  blossom: ['#3a1226', '#5a1f3a', '#a9486c', '#d9708f', '#f2a0b9', '#ffd9e4'],
};

interface Tone {
  css: string;
  rgb: readonly [number, number, number];
  lab: readonly [number, number, number];
  /** Index of the outline tone (the ramp's darkest). */
  line: number;
  /** A dark tone (OKLab L < 0.35): faint washes of it are dropped, not dithered. */
  dark: boolean;
}
const TONES: Tone[] = [];
for (const ramp of Object.values(FX_RAMPS)) {
  const first = TONES.length;
  for (const css of ramp) {
    const rgb = [1, 3, 5].map((i) => parseInt(css.slice(i, i + 2), 16)) as [number, number, number];
    const lab = oklab(rgb[0], rgb[1], rgb[2]);
    TONES.push({ css, rgb, lab, line: first, dark: lab[0] < 0.35 });
  }
}
/** Nearest tone per 15-bit colour, filled as asked. */
const LUT = new Int16Array(32768).fill(-1);
/** The FX tone nearest to a colour (OKLab), as an index. */
export function toneOf(r: number, g: number, b: number): number {
  const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
  const hit = LUT[key]!;
  if (hit >= 0) return hit;
  const [L, A, B] = oklab((r & 0xf8) + 4, (g & 0xf8) + 4, (b & 0xf8) + 4);
  let best = 0;
  let bd = Infinity;
  for (let i = 0; i < TONES.length; i++) {
    const t = TONES[i]!.lab;
    // Lightness counts a little more than hue, so a pale core stays pale and a dark rim dark.
    const d = 1.4 * (t[0] - L) ** 2 + (t[1] - A) ** 2 + (t[2] - B) ** 2;
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  LUT[key] = best;
  return best;
}
/** The FX tone nearest to a CSS colour. */
export function snapCss(css: string): string {
  const c = parseColor(css);
  return c ? TONES[toneOf(c[0], c[1], c[2])]!.css : css;
}

/** Dither level of an alpha: 3 solid, 2 a 50 % checker, 1 a 25 % grid, 0 gone. */
// Bold: the old soft layers keep their footprint (a 0.3 glow is solid, a 0.15 one a checker, a faint one a grid).
export const levelOf = (a: number): number => (a >= 0.3 ? 3 : a >= 0.15 ? 2 : a >= 0.06 ? 1 : 0);
/** Dither level of a picture's alpha (whole a little later than shapes). */
const imageLevelOf = (a: number): number => (a >= 0.5 ? 3 : a >= 0.15 ? 2 : a >= 0.06 ? 1 : 0);
/** Whether cell (i, j) is drawn at a level (screen-locked on the grid). */
const gate = (i: number, j: number, lvl: number): boolean =>
  lvl >= 3 || (lvl === 2 ? ((i + j) & 1) === 0 : lvl === 1 ? (i & 1) === 0 && (j & 1) === 0 : false);

// ── Colour parsing ──

const COLORS = new Map<string, [number, number, number, number] | null>();
let probe: CanvasRenderingContext2D | null = null;
function parseColor(css: string): [number, number, number, number] | null {
  let c = COLORS.get(css);
  if (c !== undefined) return c;
  if (COLORS.size > 4096) COLORS.clear();
  c = null;
  const s = css.trim();
  let m: RegExpMatchArray | null;
  if (s[0] === '#') {
    const h = s.slice(1);
    if (h.length === 3 || h.length === 4) {
      const v = [...h].map((x) => parseInt(x + x, 16));
      c = [v[0]!, v[1]!, v[2]!, h.length === 4 ? v[3]! / 255 : 1];
    } else if (h.length === 6 || h.length === 8) {
      c = [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16), h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1];
    }
  } else if ((m = s.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.e+-]+%?))?\s*\)$/i))) {
    const a = m[4] === undefined ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    c = [+m[1]!, +m[2]!, +m[3]!, Number.isFinite(a) ? a : 1];
  } else if (typeof document !== 'undefined') {
    probe ??= document.createElement('canvas').getContext('2d');
    if (probe) {
      probe.fillStyle = '#000';
      probe.fillStyle = s;
      const n = String(probe.fillStyle);
      if (n !== s) c = parseColor(n);
    }
  }
  if (c && (c.some((v) => Number.isNaN(v)) || c.length !== 4)) c = null;
  COLORS.set(css, c);
  return c;
}

// ── 3. The pixel pen ──

/** A gradient recorded in user space (stops parsed), evaluated per cell when filled. */
class Grad {
  readonly stops: { at: number; c: [number, number, number, number] }[] = [];
  constructor(
    readonly radial: boolean,
    readonly args: readonly number[],
  ) {}
  addColorStop(at: number, css: string): void {
    const c = parseColor(css);
    if (!c) return;
    this.stops.push({ at, c });
    this.stops.sort((a, b) => a.at - b.at);
  }
  at(t: number): [number, number, number, number] {
    const s = this.stops;
    if (!s.length) return [0, 0, 0, 0];
    if (t <= s[0]!.at) return s[0]!.c;
    for (let i = 1; i < s.length; i++)
      if (t <= s[i]!.at) {
        const a = s[i - 1]!;
        const b = s[i]!;
        const u = (t - a.at) / Math.max(1e-6, b.at - a.at);
        return [0, 1, 2, 3].map((k) => a.c[k]! + (b.c[k]! - a.c[k]!) * u) as [number, number, number, number];
      }
    return s[s.length - 1]!.c;
  }
}

type Style = { raw: unknown; tone: number; a: number; grad?: Grad; pass?: boolean };
type M = [number, number, number, number, number, number];

const PATH_METHODS = new Set(['beginPath', 'closePath', 'moveTo', 'lineTo', 'arc', 'ellipse', 'quadraticCurveTo', 'bezierCurveTo', 'rect', 'roundRect']);
const TRANSFORMS = new Set(['translate', 'rotate', 'scale', 'setTransform', 'transform', 'resetTransform']);

/** Dithered copies of pictures faded by alpha (per picture, revision, cell size and level). */
const FADED = new WeakMap<object, Map<string, HTMLCanvasElement>>();
function fadedCopy(img: CanvasImageSource, k: number, lvl: number): CanvasImageSource {
  const src = img as HTMLCanvasElement;
  const w = src.width;
  const h = src.height;
  if (!w || !h || w * h > 512 * 512 || typeof document === 'undefined') return img;
  let byKey = FADED.get(img);
  if (!byKey) FADED.set(img, (byKey = new Map()));
  const key = `${lvl}|${k}|${canvasRevision(img)}`;
  let c = byKey.get(key);
  if (c) return c;
  if (byKey.size > 8) byKey.clear();
  c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return img;
  g.drawImage(src, 0, 0);
  const d = g.getImageData(0, 0, w, h);
  const px = d.data;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) if (!gate(Math.floor(x / k), Math.floor(y / k), lvl)) px[(y * w + x) * 4 + 3] = 0;
  g.putImageData(d, 0, 0);
  byKey.set(key, c);
  return c;
}

/**
 * P2: a cached picture moved onto the scene grid. Each `k` x `k` block of picture px becomes one cell: solid when a
 * quarter of it is opaque (shapes keep their footprint, thin lines survive), in the commonest FX tone of its opaque px;
 * a 50 % checker when it is only faint; a 1-cell outline (the ramp's darkest) round solid shapes two cells thick, except
 * where the picture already has a dark edge. One cell of margin all round for the outline. Made once per picture,
 * source rect, cell size and revision.
 */
const CLEAN = new WeakMap<object, Map<string, { c: HTMLCanvasElement } | null>>();
let scratch: HTMLCanvasElement | null = null;
export function cleanCopy(img: CanvasImageSource, sx: number, sy: number, sw: number, sh: number, k: number): { c: HTMLCanvasElement } | null {
  if (typeof document === 'undefined') return null;
  sx = Math.round(sx);
  sy = Math.round(sy);
  sw = Math.round(Math.abs(sw));
  sh = Math.round(Math.abs(sh));
  if (!sw || !sh || sw * sh > 1024 * 1024) return null;
  let byKey = CLEAN.get(img);
  if (!byKey) CLEAN.set(img, (byKey = new Map()));
  const key = `${sx},${sy},${sw},${sh},${k},${canvasRevision(img)}`;
  const hit = byKey.get(key);
  if (hit !== undefined) return hit;
  if (byKey.size > 24) byKey.clear();
  scratch ??= document.createElement('canvas');
  if (scratch.width < sw || scratch.height < sh) {
    scratch.width = Math.max(scratch.width, sw);
    scratch.height = Math.max(scratch.height, sh);
  }
  const sg = scratch.getContext('2d', { willReadFrequently: true });
  if (!sg) return null;
  sg.clearRect(0, 0, sw, sh);
  sg.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  const px = sg.getImageData(0, 0, sw, sh).data;
  const cw = Math.ceil(sw / k);
  const ch = Math.ceil(sh / k);
  const W = cw + 2;
  const H = ch + 2;
  const tone = new Int16Array(W * H).fill(-1);
  const lvl = new Uint8Array(W * H);
  const counts = new Map<number, number>();
  for (let cy = 0; cy < ch; cy++)
    for (let cx = 0; cx < cw; cx++) {
      counts.clear();
      let opaque = 0;
      let faint = 0;
      let n = 0;
      let best = -1;
      let bn = 0;
      for (let y = cy * k; y < Math.min(sh, cy * k + k); y++)
        for (let x = cx * k; x < Math.min(sw, cx * k + k); x++) {
          n++;
          const o = (y * sw + x) * 4;
          const al = px[o + 3]!;
          if (al < 32) continue;
          faint++;
          if (al < 128) continue;
          opaque++;
          const t = toneOf(px[o]!, px[o + 1]!, px[o + 2]!);
          const c = (counts.get(t) ?? 0) + 1;
          counts.set(t, c);
          // Ties go to the darker tone (an edge stays an edge).
          if (c > bn || (c === bn && TONES[t]!.lab[0] < TONES[best]!.lab[0])) {
            bn = c;
            best = t;
          }
        }
      const q = (cy + 1) * W + cx + 1;
      const quarter = Math.max(1, n / 4);
      if (opaque >= quarter && best >= 0) {
        tone[q] = best;
        lvl[q] = 3;
      } else if (faint >= quarter) {
        // Faint only: its tone from the faint px (rare; washes), as a checker.
        let r = 0;
        let g = 0;
        let b = 0;
        let m = 0;
        for (let y = cy * k; y < Math.min(sh, cy * k + k); y++)
          for (let x = cx * k; x < Math.min(sw, cx * k + k); x++) {
            const o = (y * sw + x) * 4;
            if (px[o + 3]! < 32) continue;
            r += px[o]!;
            g += px[o + 1]!;
            b += px[o + 2]!;
            m++;
          }
        if (m && ((cx + cy) & 1) === 0) {
          tone[q] = toneOf(r / m, g / m, b / m);
          lvl[q] = 2;
        }
      }
    }
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && lvl[y * W + x] === 3;
  const thick = (x: number, y: number) => {
    for (const [di, dj] of DIAG) if (solid(x + di, y) && solid(x, y + dj) && solid(x + di, y + dj)) return true;
    return false;
  };
  const out = new Int16Array(W * H).fill(-1);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      if (lvl[y * W + x] === 3) continue;
      for (const [di, dj] of NB) {
        const nx = x + di;
        const ny = y + dj;
        if (!solid(nx, ny)) continue;
        const t = tone[ny * W + nx]!;
        if (TONES[t]!.dark || !thick(nx, ny)) continue;
        out[y * W + x] = TONES[t]!.line;
        break;
      }
    }
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d');
  if (!g) {
    byKey.set(key, null);
    return null;
  }
  const id = g.createImageData(W, H);
  const d = id.data;
  for (let i = 0; i < W * H; i++) {
    const t = out[i]! >= 0 ? out[i]! : lvl[i]! ? tone[i]! : -1;
    if (t < 0) continue;
    const [r, gg, b] = TONES[t]!.rgb;
    d[i * 4] = r;
    d[i * 4 + 1] = gg;
    d[i * 4 + 2] = b;
    d[i * 4 + 3] = 255;
  }
  g.putImageData(id, 0, 0);
  const res = { c };
  byKey.set(key, res);
  return res;
}

/** An afterimage of a figure picture as a flat two-tone qi silhouette (its dark lines one tone, the rest pale), cached. */
const GHOSTS = new WeakMap<object, HTMLCanvasElement>();
export function ghostOf(img: HTMLCanvasElement): HTMLCanvasElement {
  let c = GHOSTS.get(img);
  if (c) return c;
  if (typeof document === 'undefined' || !img.width || !img.height) return img;
  c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return img;
  g.drawImage(img, 0, 0);
  const id = g.getImageData(0, 0, c.width, c.height);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3]! < 128) {
      d[i + 3] = 0;
      continue;
    }
    const dark = 0.3 * d[i]! + 0.59 * d[i + 1]! + 0.11 * d[i + 2]! < 70;
    const [r, gg, b] = dark ? [47, 106, 160] : [205, 234, 244];
    d[i] = r;
    d[i + 1] = gg;
    d[i + 2] = b;
    d[i + 3] = 255;
  }
  g.putImageData(id, 0, 0);
  GHOSTS.set(img, c);
  return c;
}

export class PixelPen {
  /** The context the effects draw on (a proxy over the world picture's). */
  readonly ctx: CanvasRenderingContext2D;
  private t!: CanvasRenderingContext2D;
  /** Canvas px per cell (the scene grid G1). */
  private res = 2;
  private cols = 0;
  private rows = 0;
  private tone = new Int16Array(0);
  private lvl = new Uint8Array(0);
  private out = new Int16Array(0);
  private x0 = 0;
  private y0 = 0;
  private x1 = -1;
  private y1 = -1;
  private alpha = 1;
  private fill: Style = { raw: '#000', tone: 0, a: 1 };
  private stroke: Style = { raw: '#000', tone: 0, a: 1 };
  private stack: { alpha: number; fill: Style; stroke: Style }[] = [];
  private m: M = [1, 0, 0, 1, 0, 0];
  private mDirty = true;
  /** Subpaths in cell coordinates, flat x, y pairs; `closed` per subpath. */
  private subs: number[][] = [];
  private closed: boolean[] = [];
  private patterns = new Map<number, CanvasPattern | null>();
  private xs: number[] = [];
  private ws: number[] = [];
  /** Cells written and pictures drawn this pass (for the lane's cost notes). */
  cells = 0;
  images = 0;

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const pen = this;
    const bound = new Map<PropertyKey, unknown>();
    this.ctx = new Proxy({} as CanvasRenderingContext2D, {
      get(_o, key) {
        const t = pen.t as unknown as Record<PropertyKey, unknown>;
        switch (key) {
          case 'fillStyle':
            return pen.fill.raw;
          case 'strokeStyle':
            return pen.stroke.raw;
          case 'globalAlpha':
            return pen.alpha;
          case 'fill':
            return (rule?: unknown) => pen.fillPath(rule === 'evenodd');
          case 'stroke':
            return () => pen.strokePath();
          case 'fillRect':
            return (x: number, y: number, w: number, h: number) => pen.fillRect(x, y, w, h);
          case 'strokeRect':
            return (x: number, y: number, w: number, h: number) => {
              const subs = pen.subs;
              const cl = pen.closed;
              pen.subs = [];
              pen.closed = [];
              pen.rectPath(x, y, w, h);
              pen.strokePath();
              pen.subs = subs;
              pen.closed = cl;
            };
          case 'drawImage':
            return (...a: unknown[]) => pen.drawImage(a);
          case 'save':
            return () => {
              pen.stack.push({ alpha: pen.alpha, fill: pen.fill, stroke: pen.stroke });
              pen.t.save();
              pen.mDirty = true;
            };
          case 'restore':
            return () => {
              const s = pen.stack.pop();
              if (s) {
                pen.alpha = s.alpha;
                pen.fill = s.fill;
                pen.stroke = s.stroke;
              }
              pen.t.restore();
              pen.mDirty = true;
            };
          case 'createLinearGradient':
            return (...a: number[]) => new Grad(false, a);
          case 'createRadialGradient':
            return (...a: number[]) => new Grad(true, a);
          case 'fillText':
          case 'strokeText':
          case 'outlinedText':
            return (...a: unknown[]) => {
              pen.flush();
              pen.t.globalAlpha = pen.alpha;
              if (key === 'strokeText') pen.t.strokeStyle = pen.stroke.raw as string;
              else pen.t.fillStyle = pen.fill.raw as string;
              (t[key] as (...x: unknown[]) => void)(...a);
              pen.t.globalAlpha = 1;
            };
        }
        if (typeof key === 'string' && PATH_METHODS.has(key)) return (...a: number[]) => pen.path(key, a);
        if (typeof key === 'string' && TRANSFORMS.has(key))
          return (...a: unknown[]) => {
            pen.mDirty = true;
            (t[key] as (...x: unknown[]) => void)(...a);
          };
        const v = t[key];
        if (typeof v !== 'function') return v;
        if (!bound.has(key)) bound.set(key, (...a: unknown[]) => (pen.t as unknown as Record<PropertyKey, (...x: unknown[]) => unknown>)[key as string]!(...a));
        return bound.get(key);
      },
      set(_o, key, value) {
        if (key === 'fillStyle') pen.fill = pen.style(value);
        else if (key === 'strokeStyle') pen.stroke = pen.style(value);
        else if (key === 'globalAlpha') pen.alpha = Number.isFinite(value) ? Math.max(0, Math.min(1, value as number)) : pen.alpha;
        else (pen.t as unknown as Record<PropertyKey, unknown>)[key] = value;
        return true;
      },
    });
  }

  /** Starts a pass on `target` (its transform: art px → canvas px, the scale being the grid's `res`). */
  begin(target: CanvasRenderingContext2D): CanvasRenderingContext2D {
    this.t = target;
    const tm = target.getTransform();
    const res = Math.max(1, Math.round(Math.hypot(tm.a, tm.b)));
    if (res !== this.res) this.patterns.clear();
    this.res = res;
    const cv = target.canvas as { width: number; height: number } | undefined;
    const cols = Math.ceil((cv?.width ?? 2048) / this.res) + 2;
    const rows = Math.ceil((cv?.height ?? 2048) / this.res) + 2;
    if (cols !== this.cols || rows !== this.rows) {
      this.cols = cols;
      this.rows = rows;
      this.tone = new Int16Array(cols * rows).fill(-1);
      this.lvl = new Uint8Array(cols * rows);
      this.out = new Int16Array(cols * rows).fill(-1);
    }
    this.alpha = 1;
    this.fill = { raw: '#000', tone: toneOf(0, 0, 0), a: 1 };
    this.stroke = this.fill;
    this.stack = [];
    this.subs = [];
    this.closed = [];
    this.mDirty = true;
    this.x1 = this.y1 = -1;
    this.cells = 0;
    this.images = 0;
    return this.ctx;
  }

  /** Ends the pass: writes what is left. */
  end(): void {
    this.flush();
    STATS.cells += this.cells;
    STATS.images += this.images;
    if (this.stack.length) for (let i = this.stack.length; i > 0; i--) this.t.restore();
    this.stack = [];
  }

  private style(v: unknown): Style {
    if (v instanceof Grad) return { raw: v, tone: 0, a: 1, grad: v };
    if (typeof v !== 'string') return { raw: v, tone: 0, a: 1, pass: true };
    const c = parseColor(v);
    if (!c) return { raw: v, tone: 0, a: 1, pass: true };
    return { raw: v, tone: toneOf(c[0], c[1], c[2]), a: c[3] };
  }

  private mat(): M {
    if (this.mDirty) {
      const d = this.t.getTransform();
      const r = this.res;
      // Cell space: canvas px / res.
      this.m = [d.a / r, d.b / r, d.c / r, d.d / r, d.e / r, d.f / r];
      this.mDirty = false;
    }
    return this.m;
  }

  // ── Paths ──

  private cur(): number[] {
    let s = this.subs[this.subs.length - 1];
    if (!s) {
      s = [];
      this.subs.push(s);
      this.closed.push(false);
    }
    return s;
  }
  private add(x: number, y: number): void {
    const m = this.mat();
    this.cur().push(m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]);
  }
  private rectPath(x: number, y: number, w: number, h: number): void {
    this.subs.push([]);
    this.closed.push(true);
    this.add(x, y);
    this.add(x + w, y);
    this.add(x + w, y + h);
    this.add(x, y + h);
    this.subs.push([]);
    this.closed.push(false);
  }
  private path(name: string, a: number[]): void {
    switch (name) {
      case 'beginPath':
        this.subs = [];
        this.closed = [];
        return;
      case 'moveTo':
        this.subs.push([]);
        this.closed.push(false);
        this.add(a[0]!, a[1]!);
        return;
      case 'lineTo':
        this.add(a[0]!, a[1]!);
        return;
      case 'closePath': {
        const s = this.subs[this.subs.length - 1];
        if (s && s.length) {
          this.closed[this.closed.length - 1] = true;
          this.subs.push([s[0]!, s[1]!]);
          this.closed.push(false);
        }
        return;
      }
      case 'rect':
      case 'roundRect':
        this.rectPath(a[0]!, a[1]!, a[2]!, a[3]!);
        return;
      case 'arc':
        this.arc(a[0]!, a[1]!, a[2]!, a[2]!, 0, a[3]!, a[4]!, !!a[5]);
        return;
      case 'ellipse':
        this.arc(a[0]!, a[1]!, a[2]!, a[3]!, a[4]!, a[5]!, a[6]!, !!a[7]);
        return;
      case 'quadraticCurveTo':
      case 'bezierCurveTo': {
        const s = this.cur();
        const m = this.mat();
        // The last point back in user space is not kept; curves are flattened in cell space (affine-invariant).
        const px = s[s.length - 2];
        const py = s[s.length - 1];
        const tp = (x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
        if (px === undefined || py === undefined) {
          this.add(a[a.length - 2]!, a[a.length - 1]!);
          return;
        }
        const pts: [number, number][] = [[px, py]];
        for (let i = 0; i < a.length; i += 2) pts.push(tp(a[i]!, a[i + 1]!));
        const n = 10;
        for (let k = 1; k <= n; k++) {
          const u = k / n;
          let q = pts.map((p) => [p[0], p[1]] as [number, number]);
          while (q.length > 1) q = q.slice(1).map((p, i) => [q[i]![0] + (p[0] - q[i]![0]) * u, q[i]![1] + (p[1] - q[i]![1]) * u] as [number, number]);
          s.push(q[0]![0], q[0]![1]);
        }
        return;
      }
    }
  }
  private arc(x: number, y: number, rx: number, ry: number, rot: number, a0: number, a1: number, ccw: boolean): void {
    if (!(rx >= 0) || !(ry >= 0)) return;
    const TAU = Math.PI * 2;
    let sweep: number;
    if (!ccw) {
      sweep = a1 - a0;
      if (sweep >= TAU) sweep = TAU;
      else {
        sweep %= TAU;
        if (sweep < 0) sweep += TAU;
      }
    } else {
      sweep = a1 - a0;
      if (sweep <= -TAU) sweep = -TAU;
      else {
        sweep %= TAU;
        if (sweep > 0) sweep -= TAU;
      }
    }
    const m = this.mat();
    const scale = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
    const n = Math.max(6, Math.min(72, Math.ceil(Math.max(rx, ry) * scale * Math.abs(sweep) * 0.9)));
    const cr = Math.cos(rot);
    const sr = Math.sin(rot);
    for (let i = 0; i <= n; i++) {
      const th = a0 + (sweep * i) / n;
      const lx = rx * Math.cos(th);
      const ly = ry * Math.sin(th);
      this.add(x + lx * cr - ly * sr, y + lx * sr + ly * cr);
    }
  }

  // ── Rasterising ──

  private put(i: number, j: number, tone: number, lvl: number): void {
    if (i < 0 || j < 0 || i >= this.cols || j >= this.rows || lvl <= 0) return;
    // A faint dark wash (a shade or a dim under a move) would be a field of dark dots: dropped.
    if (lvl < 3 && TONES[tone]!.dark) return;
    const k = j * this.cols + i;
    const old = this.lvl[k]!;
    if (lvl < 3 && old === 3) {
      // A faint layer over a solid one: its own cells only.
      if (!gate(i, j, lvl)) return;
      lvl = 3;
    }
    this.tone[k] = tone;
    this.lvl[k] = lvl;
    this.cells++;
    if (this.x1 < 0) {
      this.x0 = this.x1 = i;
      this.y0 = this.y1 = j;
    } else {
      if (i < this.x0) this.x0 = i;
      if (i > this.x1) this.x1 = i;
      if (j < this.y0) this.y0 = j;
      if (j > this.y1) this.y1 = j;
    }
  }
  /** Writes a cell in a style (a gradient is evaluated at the cell's centre). */
  private putStyled(i: number, j: number, s: Style): void {
    if (s.grad) {
      const c = this.gradAt(s.grad, i + 0.5, j + 0.5);
      this.put(i, j, toneOf(c[0], c[1], c[2]), levelOf(c[3] * this.alpha));
    } else this.put(i, j, s.tone, levelOf(s.a * this.alpha));
  }
  private gradAt(g: Grad, x: number, y: number): [number, number, number, number] {
    // The gradient's own points in cell space (canvas rule: the transform at fill time).
    const m = this.mat();
    const a = g.args;
    const tp = (u: number, v: number): [number, number] => [m[0] * u + m[2] * v + m[4], m[1] * u + m[3] * v + m[5]];
    if (!g.radial) {
      const [x0, y0] = tp(a[0]!, a[1]!);
      const [x1, y1] = tp(a[2]!, a[3]!);
      const dx = x1 - x0;
      const dy = y1 - y0;
      const l2 = dx * dx + dy * dy || 1;
      return g.at(Math.max(0, Math.min(1, ((x - x0) * dx + (y - y0) * dy) / l2)));
    }
    const sc = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
    const [cx, cy] = tp(a[3]!, a[4]!);
    const r0 = a[2]! * sc;
    const r1 = a[5]! * sc;
    return g.at(Math.max(0, Math.min(1, (Math.hypot(x - cx, y - cy) - r0) / Math.max(1e-6, r1 - r0))));
  }
  private styleLive(s: Style): boolean {
    return !!s.grad || levelOf(s.a * this.alpha) > 0;
  }

  private fillRect(x: number, y: number, w: number, h: number): void {
    if (!w || !h) return;
    if (this.fill.pass) {
      this.flush();
      this.t.globalAlpha = this.alpha;
      this.t.fillStyle = this.fill.raw as string;
      this.t.fillRect(x, y, w, h);
      this.t.globalAlpha = 1;
      return;
    }
    if (!this.styleLive(this.fill)) return;
    const m = this.mat();
    if (m[1] === 0 && m[2] === 0) {
      let X0 = m[0] * x + m[4];
      let X1 = m[0] * (x + w) + m[4];
      let Y0 = m[3] * y + m[5];
      let Y1 = m[3] * (y + h) + m[5];
      if (X1 < X0) [X0, X1] = [X1, X0];
      if (Y1 < Y0) [Y0, Y1] = [Y1, Y0];
      let i0 = Math.ceil(X0 - 0.5);
      let i1 = Math.floor(X1 - 0.5);
      let j0 = Math.ceil(Y0 - 0.5);
      let j1 = Math.floor(Y1 - 0.5);
      // Thinner than a cell: the cell under its middle (no sub-grid specks, none lost).
      if (i1 < i0) i0 = i1 = Math.floor((X0 + X1) / 2);
      if (j1 < j0) j0 = j1 = Math.floor((Y0 + Y1) / 2);
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) this.putStyled(i, j, this.fill);
      return;
    }
    const subs = this.subs;
    const cl = this.closed;
    this.subs = [];
    this.closed = [];
    this.rectPath(x, y, w, h);
    this.fillPath(false);
    this.subs = subs;
    this.closed = cl;
  }

  private fillPath(evenOdd: boolean): void {
    if (this.fill.pass) return;
    if (!this.styleLive(this.fill)) return;
    const subs = this.subs.filter((s) => s.length >= 4);
    if (!subs.length) return;
    let minY = Infinity;
    let maxY = -Infinity;
    let minX = Infinity;
    let maxX = -Infinity;
    for (const s of subs)
      for (let k = 0; k < s.length; k += 2) {
        const px = s[k]!;
        const py = s[k + 1]!;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
      }
    if (maxY - minY > 4000 || maxX - minX > 4000) return;
    const xs = this.xs;
    const ws = this.ws;
    let any = false;
    for (let j = Math.ceil(minY - 0.5); j <= Math.floor(maxY - 0.5); j++) {
      const yc = j + 0.5;
      xs.length = 0;
      ws.length = 0;
      for (const s of subs) {
        const n = s.length / 2;
        for (let k = 0; k < n; k++) {
          const ax = s[k * 2]!;
          const ay = s[k * 2 + 1]!;
          const kb = (k + 1) % n;
          const bx = s[kb * 2]!;
          const by = s[kb * 2 + 1]!;
          if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) {
            xs.push(ax + ((yc - ay) * (bx - ax)) / (by - ay));
            ws.push(by > ay ? 1 : -1);
          }
        }
      }
      if (xs.length < 2) continue;
      // Insertion sort (few crossings), keeping the windings with them.
      for (let a = 1; a < xs.length; a++) {
        const vx = xs[a]!;
        const vw = ws[a]!;
        let b = a - 1;
        while (b >= 0 && xs[b]! > vx) {
          xs[b + 1] = xs[b]!;
          ws[b + 1] = ws[b]!;
          b--;
        }
        xs[b + 1] = vx;
        ws[b + 1] = vw;
      }
      let wind = 0;
      for (let a = 0; a < xs.length - 1; a++) {
        wind += evenOdd ? 1 : ws[a]!;
        const inside = evenOdd ? (wind & 1) === 1 : wind !== 0;
        if (!inside) continue;
        const i0 = Math.ceil(xs[a]! - 0.5);
        const i1 = Math.floor(xs[a + 1]! - 0.5);
        for (let i = i0; i <= i1; i++) {
          this.putStyled(i, j, this.fill);
          any = true;
        }
      }
    }
    // Smaller than a cell: the cell under its middle.
    if (!any) this.putStyled(Math.floor((minX + maxX) / 2), Math.floor((minY + maxY) / 2), this.fill);
  }

  private strokePath(): void {
    if (this.stroke.pass) return;
    if (!this.styleLive(this.stroke)) return;
    const m = this.mat();
    const scale = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
    // At least two cells: an arc or a trail stays a bold band (and gets its outline), never a hairline.
    const wc = Math.max(2, Math.round((this.t.lineWidth || 1) * scale));
    const off = Math.floor((wc - 1) / 2);
    const stamp = (cx: number, cy: number) => {
      for (let j = 0; j < wc; j++) for (let i = 0; i < wc; i++) this.putStyled(cx - off + i, cy - off + j, this.stroke);
    };
    this.subs.forEach((s, si) => {
      const n = s.length / 2;
      if (n < 1) return;
      const pts = this.closed[si] && n > 2 ? [...s, s[0]!, s[1]!] : s;
      if (n === 1) {
        stamp(Math.floor(s[0]!), Math.floor(s[1]!));
        return;
      }
      for (let k = 0; k + 3 < pts.length; k += 2) {
        const ax = pts[k]!;
        const ay = pts[k + 1]!;
        const bx = pts[k + 2]!;
        const by = pts[k + 3]!;
        const steps = Math.max(1, Math.ceil(Math.max(Math.abs(bx - ax), Math.abs(by - ay))));
        if (steps > 4000) continue;
        for (let q = 0; q <= steps; q++) {
          const u = q / steps;
          stamp(Math.floor(ax + (bx - ax) * u), Math.floor(ay + (by - ay) * u));
        }
      }
    });
  }

  // ── Pictures ──

  private drawImage(a: unknown[]): void {
    this.flush();
    const lvl = imageLevelOf(this.alpha);
    if (lvl === 0) return;
    const img = a[0] as CanvasImageSource & { width: number; height: number };
    const n = a.length;
    let sx = 0;
    let sy = 0;
    let sw = img.width;
    let sh = img.height;
    let dx: number;
    let dy: number;
    let dw: number;
    let dh: number;
    if (n >= 9) {
      [sx, sy, sw, sh, dx, dy, dw, dh] = a.slice(1, 9) as number[] as [number, number, number, number, number, number, number, number];
    } else if (n >= 5) {
      [dx, dy, dw, dh] = a.slice(1, 5) as number[] as [number, number, number, number];
    } else {
      [dx, dy] = a.slice(1, 3) as number[] as [number, number];
      dw = sw;
      dh = sh;
    }
    if (!sw || !sh || !dw || !dh) return;
    const m = this.mat();
    const scale = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
    // Picture px per cell.
    const k = Math.max(1, Math.round(Math.abs(sw) / Math.max(1e-6, Math.abs(dw) * scale)));
    // P2: the picture on the scene grid, in the FX ramps, outlined (cached once per picture).
    let src: CanvasImageSource = img;
    const clean = cleanCopy(img, sx, sy, sw, sh, k);
    if (clean) {
      const cw = dw / (sw / k);
      const ch = dh / (sh / k);
      src = clean.c;
      dx -= cw;
      dy -= ch;
      dw = clean.c.width * cw;
      dh = clean.c.height * ch;
      sx = 0;
      sy = 0;
      sw = clean.c.width;
      sh = clean.c.height;
    }
    // Snap the picture's corner to the scene grid when it is drawn upright (flips included).
    if (m[1] === 0 && m[2] === 0 && m[0] !== 0 && m[3] !== 0) {
      dx += (Math.round(m[0] * dx + m[4]) - (m[0] * dx + m[4])) / m[0];
      dy += (Math.round(m[3] * dy + m[5]) - (m[3] * dy + m[5])) / m[3];
    }
    if (lvl < 3) src = fadedCopy(src, clean ? 1 : k, lvl);
    this.t.globalAlpha = 1;
    this.t.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
    this.images++;
  }

  // ── Output ──

  private pattern(tone: number, lvl: number): CanvasPattern | null {
    const key = tone * 4 + lvl;
    let p = this.patterns.get(key);
    if (p !== undefined) return p;
    p = null;
    if (typeof document !== 'undefined') {
      const r = this.res;
      const c = document.createElement('canvas');
      c.width = c.height = 2 * r;
      // A CPU-backed tile: a GPU-backed one used as a pattern on a CPU canvas (willReadFrequently, render/pixel.ts
      // ctx2d) is read back per fill when the canvas flushes (seconds per dithered effect); on GPU targets it costs
      // the same either way.
      const g = c.getContext('2d', { willReadFrequently: true });
      if (g) {
        g.fillStyle = TONES[tone]!.css;
        for (let j = 0; j < 2; j++) for (let i = 0; i < 2; i++) if (gate(i, j, lvl)) g.fillRect(i * r, j * r, r, r);
        p = this.t.createPattern(c, 'repeat');
      }
    }
    this.patterns.set(key, p);
    return p;
  }

  /** Whether solid cell (i, j) lies in a solid 2 x 2 block. */
  private thick(i: number, j: number): boolean {
    const cols = this.cols;
    const L = this.lvl;
    const s = (a: number, b: number) => a >= 0 && b >= 0 && a < cols && b < this.rows && L[b * cols + a] === 3;
    for (const [di, dj] of DIAG) if (s(i + di, j) && s(i, j + dj) && s(i + di, j + dj)) return true;
    return false;
  }

  /** Writes the cells (and their outline) as hard runs, then clears them. */
  flush(): void {
    if (this.x1 < 0) return;
    const cols = this.cols;
    const r = this.res;
    const X0 = Math.max(0, this.x0 - 1);
    const X1 = Math.min(cols - 1, this.x1 + 1);
    const Y0 = Math.max(0, this.y0 - 1);
    const Y1 = Math.min(this.rows - 1, this.y1 + 1);
    const tone = this.tone;
    const lvl = this.lvl;
    const out = this.out;
    // Outline: an empty cell beside a solid cell that is not a lone glint takes that cell's ramp's darkest tone.
    for (let j = Y0; j <= Y1; j++)
      for (let i = X0; i <= X1; i++) {
        const k = j * cols + i;
        out[k] = -1;
        if (lvl[k] === 3) continue;
        for (const [di, dj] of NB) {
          const ni = i + di;
          const nj = j + dj;
          if (ni < X0 || nj < Y0 || ni > X1 || nj > Y1) continue;
          const nk = nj * cols + ni;
          if (lvl[nk] !== 3) continue;
          // Only shapes at least two cells thick get the ring (the cell is in a solid 2 x 2 block): a spark or a
          // 1-px line stays a bare glint or streak.
          if (!this.thick(ni, nj)) continue;
          out[k] = TONES[tone[nk]!]!.line;
          break;
        }
      }
    const t = this.t;
    t.save();
    t.setTransform(1, 0, 0, 1, 0, 0);
    t.globalAlpha = 1;
    for (let j = Y0; j <= Y1; j++) {
      let i = X0;
      while (i <= X1) {
        const k = j * cols + i;
        const o = out[k]!;
        const L = o >= 0 ? 3 : lvl[k]!;
        const T = o >= 0 ? o : tone[k]!;
        if (L === 0) {
          i++;
          continue;
        }
        let e = i + 1;
        while (e <= X1) {
          const q = j * cols + e;
          const o2 = out[q]!;
          if ((o2 >= 0 ? 3 : lvl[q]!) !== L || (o2 >= 0 ? o2 : tone[q]!) !== T) break;
          e++;
        }
        if (L === 3) t.fillStyle = TONES[T]!.css;
        else {
          const p = this.pattern(T, L);
          if (!p) {
            i = e;
            continue;
          }
          t.fillStyle = p;
        }
        t.fillRect(i * r, j * r, (e - i) * r, r);
        i = e;
      }
      for (let i2 = X0; i2 <= X1; i2++) {
        const k = j * cols + i2;
        tone[k] = -1;
        lvl[k] = 0;
        out[k] = -1;
      }
    }
    t.restore();
    this.x1 = this.y1 = -1;
  }
}

const NB: readonly (readonly [number, number])[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
const DIAG: readonly (readonly [number, number])[] = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

// ── 4. Shapes the rules draw themselves ──

/**
 * A column of light as pixel art (replaces the soft gradient beams): a flat body in `col` ("r,g,b"), its right third a
 * lit tone, a pale core in the lower part, and the top thinned out in dither bands (solid, checker, sparse), all
 * scaled by `a`. `x` is the centre, `y` the foot, art px.
 */
export function drawColumn(g: CanvasRenderingContext2D, x: number, y: number, h: number, w: number, col: string, a: number): void {
  if (a <= 0) return;
  const [r, gg, b] = col.split(',').map(Number) as [number, number, number];
  const lit = `${Math.round(r + (255 - r) * 0.3)},${Math.round(gg + (255 - gg) * 0.3)},${Math.round(b + (255 - b) * 0.3)}`;
  const W = Math.max(2, Math.round(w));
  const x0 = Math.round(x - W / 2);
  const lw = Math.max(1, Math.round(W / 3));
  const bands: readonly (readonly [number, number, number])[] = [
    [0, 0.45, 1],
    [0.45, 0.7, 0.4],
    [0.7, 0.9, 0.18],
  ];
  for (const [u0, u1, k] of bands) {
    const yt = Math.round(y - h * u1);
    const hh = Math.round(y - h * u0) - yt;
    if (hh <= 0) continue;
    g.fillStyle = `rgba(${col},${(a * k).toFixed(3)})`;
    g.fillRect(x0, yt, W - lw, hh);
    g.fillStyle = `rgba(${lit},${(a * k).toFixed(3)})`;
    g.fillRect(x0 + W - lw, yt, lw, hh);
  }
  const cw = Math.max(1, Math.round(W / 6));
  g.fillStyle = `rgba(253,246,236,${(a * 0.9).toFixed(3)})`;
  g.fillRect(Math.round(x - cw / 2), Math.round(y - h * 0.4), cw, Math.round(h * 0.4));
}
